<template>
  <div class="school">
    <div class="school-stat">
      <div class="school-stat-list">
        <div
          v-for="item in schoolStatList"
          :key="item.name"
          class="school-stat-list-item"
          :style="{ background: `url(${item.bgUrl})` }"
        >
          <div class="school-stat-list-item-type">{{ item.name }}</div>
          <div class="school-stat-list-item-total">{{ item.total }}</div>
        </div>
      </div>
      <div v-loading="!loading" class="school-stat-chart">
        <EchartBox
          v-if="loading"
          routeName="School"
          echartType="校本、社团学生增长趋势"
          :areaId="areaId"
          :regionId="regionId"
          :getStatFunc="getSchoolStat"
          needFilterType="unit"
          :boxHeight="470"
        />
      </div>
      <div v-loading="!loading" class="school-stat-chart">
        <EchartBox
          v-if="loading"
          routeName="School"
          echartType="院校占比"
          :areaId="areaId"
          :regionId="regionId"
          :getStatFunc="getSchoolProportionStat"
          :boxHeight="470"
        />
      </div>
    </div>
    <div class="school-table">
      <CommonTable
        ref="school-table"
        tableType="学校详情"
        :keys="schoolKeys"
        :count="count"
        :list="schoolList"
        :btns="schoolBtns"
        :needExport="true"
        :exportStat="tableFooter"
        @changePagingParams="changePagingParams"
        @download="download"
        @onSortChange="onSortChange"
      >
        <template v-slot:table-filter>
          <div class="school-table-filter">
            <SelectCom
              placeholder="请选择学校类型"
              v-model="filterParams.type"
              :options="schoolTypes"
            />
            <CommonInput
              v-model="filterParams.name"
              placeholder="按学校名称搜索"
            />
          </div>
        </template>
      </CommonTable>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import moment from 'moment'
import _ from 'lodash'
import { exportXlsx, formatDimTimeId, getYearWeek } from '@/utils/common'
import EchartBox from '../common/EchartBox'
import CommonTable from '../common/CommonTable'
import SelectCom from '../common/SelectCom'
import CommonInput from '../common/CommonInput'

export default {
  name: 'School',
  components: {
    EchartBox,
    CommonTable,
    SelectCom,
    CommonInput,
  },
  props: {
    regionId: [String, Number],
    areaId: [String, Number],
    filterTimes: Object,
  },
  data() {
    return {
      loading: false,
      filterParams: {
        orderBy: '',
        order: '',
        type: '',
        name: '',
      },
      pagingParams: {
        page: 1,
        pageSize: 10,
      },
      schoolTypes: [
        {
          value: 'isCampusActive',
          label: '校本',
        },
        {
          value: 'is430Active',
          label: '社团',
        },
      ],
      schoolKeys: [
        {
          prop: 'schoolName',
          label: '学校名',
        },
        {
          prop: 'regionFullname',
          label: '区域',
        },
        {
          prop: 'freeCampusStudentCount',
          label: '校本学生数',
          align: 'right',
          sortable: 'custom',
        },
        {
          prop: 'pay430StudentCount',
          label: '社团学生数',
          align: 'right',
          sortable: 'custom',
        },
        {
          prop: 'activeStudentTotal',
          label: '激活学生数',
          align: 'right',
          sortable: 'custom',
        },
      ],
      count: 0,
      schoolList: [],
      schoolBtns: [
        {
          text: '查看详情',
          onClick: row => {
            this.$router.push({
              name: 'SchoolDetail',
              query: {
                areaId: this.areaId,
                orgId: row.orgId,
                name: row.schoolName,
                schoolId: row.schoolId,
              },
            })
          },
        },
      ],
      schoolStatList: [],
      tableFooter: {},
    }
  },
  computed: {
    filterStatParams() {
      return {
        regionId: this.regionId,
        ...this.filterTimes,
      }
    },
  },
  activated() {
    this.init()
  },
  watch: {
    regionId: {
      handler(regionId) {
        if (regionId) {
          this.loading = false
          this.$nextTick(() => {
            this.loading = true
            this.init()
          })
        }
      },
      immediate: true,
    },
    filterParams: {
      deep: true,
      handler() {
        this.$refs['school-table'].handleSizeChange(10)
        this.debounce()
      },
    },
    pagingParams: {
      deep: true,
      handler() {
        this.debounce()
      },
    },
    filterStatParams: {
      handler(params) {
        if (params.regionId) {
          const statParams = {
            ...params,
            unit: params.unit === 'all' ? 'day' : params.unit,
          }
          this.initShoolStatList(statParams)
        }
      },
      immediate: true,
    },
  },
  created() {
    const type = this.$route.query.type
    if (type) {
      this.filterParams.type = type
    }
  },
  methods: {
    ...mapActions({
      getRegionStats: 'newStat/getRegionStats',
      getSchoolList: 'newStat/getSchoolList',
    }),
    async init() {
      await this.onGetSchoolList()
    },
    async initShoolStatList(params) {
      const stats = await this.getRegionStats(params)
      const stat = stats[0] || {}
      this.schoolStatList = [
        {
          name: '校本学校数',
          total: stat.schoolCampusCount || 0,
          bgUrl: require('@/assets/common/free.png'),
        },
        {
          name: '社团学校数',
          total: stat.school430Count || 0,
          bgUrl: require('@/assets/common/pay.png'),
        },
        {
          name: '校本转社团学校数',
          total: stat.campusTo430SchoolCount || 0,
          bgUrl: require('@/assets/common/free-pay.png'),
        },
      ]
    },
    async getSchoolStat(params) {
      const stats = await this.getRegionStats(params)
      const legendData = ['校本', '社团']
      let xAxisData = []
      const startTime = moment(params.startTime)
      const endTime = moment(params.endTime)
      const seriesData = [
        {
          name: '校本',
          type: 'line',
          data: [],
        },
        {
          name: '社团',
          type: 'line',
          data: [],
        },
      ]
      function formatStat(data) {
        data.forEach(item => {
          const curStat = stats.find(
            stat => formatDimTimeId(stat.dimTimeId) === item
          )
          if (curStat) {
            seriesData[0].data.push(curStat.freeCampusStudentIncrement || 0)
            seriesData[1].data.push(curStat.pay430StudentIncrement || 0)
          } else {
            seriesData[0].data.push(0)
            seriesData[1].data.push(0)
          }
        })
      }
      if (params.unit === 'day') {
        while (startTime <= endTime) {
          xAxisData.push(startTime.format('YYYY-MM-DD'))
          startTime.add(1, 'days')
        }
        formatStat(xAxisData)
      }
      if (params.unit === 'week') {
        let startWeeks = getYearWeek(startTime)
        const endWeeks = getYearWeek(endTime)
        while (startWeeks < endWeeks) {
          xAxisData.push(startTime.endOf('isoweek').format('YYYY-MM-DD'))
          startWeeks += 1
          startTime.add(1, 'weeks')
        }
        formatStat(xAxisData)
      }
      if (params.unit === 'month') {
        let startMonth = _.toNumber(startTime.format('MM'))
        const endMonth = _.toNumber(endTime.format('MM'))
        const xData = []
        while (startMonth < endMonth) {
          xData.push(startTime.endOf('month').format('YYYY-MM-DD'))
          xAxisData.push(`${startMonth}月`)
          startMonth += 1
        }
        formatStat(xData)
      }
      return {
        echartEl: 'school-line-stat',
        hasStat: xAxisData.length > 0,
        echartOptions: {
          tooltip: {
            trigger: 'axis',
          },
          legend: {
            data: legendData,
            bottom: '5%',
          },
          grid: {
            left: '6%',
            right: '6%',
            bottom: '14%',
            containLabel: true,
          },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: xAxisData,
          },
          yAxis: {
            type: 'value',
            minInterval: 1,
            axisTick: { show: false },
            axisLine: { show: false },
            splitLine: {
              lineStyle: {
                type: 'dashed',
              },
            },
          },
          series: seriesData,
        },
      }
    },
    async getSchoolProportionStat(params) {
      const stats = await this.getRegionStats(params)
      const stat = stats[0] || {}
      const xAxisData = ['校本院校', '社团院校']
      const seriesData = [
        { value: stat.schoolCampusCount || 0, name: '校本院校' },
        { value: stat.school430Count || 0, name: '社团院校' },
      ]
      this.tableFooter = {
        campusStudentCount: stat.freeCampusStudentCount || 0,
        studentCount430: stat.pay430StudentCount || 0,
        orgStudentCount: stat.payOrgStudentCount || 0,
      }
      return {
        echartEl: 'school-pie-stat',
        echartOptions: {
          tooltip: {
            trigger: 'item',
            formatter: '{b}<br/>{c} ({d}%)',
          },
          legend: {
            data: xAxisData,
            bottom: '5%',
          },
          series: {
            type: 'pie',
            radius: '50%',
            center: ['50%', '50%'],
            label: {
              normal: {
                formatter: '{b}\n{d}%',
              },
            },
            data: seriesData,
          },
        },
      }
    },
    debounce: _.debounce(function () {
      this.onGetSchoolList()
    }, 300),
    async onGetSchoolList() {
      const { count, rows } = await this.onGetSchoolListFunc()
      this.count = count
      this.schoolList = rows
    },
    async onGetSchoolListFunc(noPage = false) {
      let params = {
        areaId: this.areaId,
        regionId: this.regionId,
        noPage,
        isActive: false,
      }
      if (noPage === false) {
        params = {
          ...params,
          'x-page': this.pagingParams.page,
          'x-per-page': this.pagingParams.pageSize,
        }
      }
      if (this.filterParams.type) {
        params.type = this.filterParams.type
      }
      if (this.filterParams.name) {
        params.name = this.filterParams.name
      }
      if (this.filterParams.order) {
        params.sortDirect = this.filterParams.order
      }
      if (this.filterParams.orderBy) {
        params.sortField = this.filterParams.orderBy
      }
      const { count, rows } = await this.getSchoolList(params)
      const schoolList = rows.map(item => {
        return {
          orgId: item.orgId,
          schoolId: item.schoolId,
          schoolName: item.schoolName,
          regionFullname: item.regionFullname,
          activeStudentTotal: item.activeStudentTotal,
          freeCampusStudentCount: item.freeCampusStudentCount,
          pay430StudentCount: item.pay430StudentCount || 0,
          is430Active: item.is430Active,
          isCampusActive: item.isCampusActive,
        }
      })
      return {
        count,
        rows: schoolList,
      }
    },
    async download() {
      const { rows } = await this.onGetSchoolListFunc(true)
      if (rows.length > 0) {
        exportXlsx('学校列表', this.schoolKeys, rows)
      } else {
        this.$message.warning('暂时没有数据')
      }
    },
    changePagingParams(paging) {
      this.pagingParams = paging
    },
    changeAreaId(areaId) {
      this.areaId = areaId
    },
    onSortChange(params) {
      this.filterParams = {
        ...this.filterParams,
        ...params,
      }
    },
  },
}
</script>

<style lang='scss'>
.school {
  .school-stat {
    display: flex;
    margin: 32px 0px 20px;
    justify-content: space-between;
    .school-stat-chart {
      width: 41%;
    }
    .school-stat-list {
      width: 15%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      .school-stat-list-item {
        background-color: #31adff;
        box-shadow: 0px 0px 10px 0px rgba(92, 92, 93, 0.1);
        border-radius: 4px;
        height: 142px;
        padding: 28px 28px;
        box-sizing: border-box;
        color: #fff;
        background: url('../../assets/common/free.png');
        .school-stat-list-item-total {
          font-size: 40px;
          margin-top: 16px;
        }
      }
    }
  }
  .school-table-filter {
    display: flex;
    width: 100%;
    justify-content: space-between;
  }
}
</style>